import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Trash2, BarChart3, Utensils, ClipboardList, CalendarCheck, Boxes } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { InlineLoader } from "@/components/common/Loader";
import { OPS } from "@/constants/testIds";
import { EntryTab } from "@/components/waste-meals/EntryTab";
import { MonthlyGrid } from "@/components/waste-meals/MonthlyGrid";
import { DayCoverageTable } from "@/components/waste-meals/DayCoverageTable";
import { AdminTab } from "@/components/waste-meals/admin/AdminTab";

const C = OPS.wm;

const triggerClass =
    "gap-2 rounded-full px-4 py-1.5 text-sm font-semibold text-[color:var(--ink-muted)] transition-[color,background-color] duration-150 ease-out hover:text-[color:var(--ink)] hover:bg-[color:var(--brand-orange-soft)] data-[state=active]:bg-[color:var(--brand-orange)] data-[state=active]:text-white data-[state=active]:shadow-[var(--shadow-sm)]";

const TAB_DEFS = {
    waste: { icon: Trash2, label: "wm_tab_waste", render: (s) => <EntryTab scope={s} type="waste" /> },
    waste_report: { icon: BarChart3, label: "wm_tab_waste_report", render: (s) => <MonthlyGrid scope={s} type="waste" /> },
    meals: { icon: Utensils, label: "wm_tab_meals", render: (s) => <EntryTab scope={s} type="meals" /> },
    meals_report: { icon: ClipboardList, label: "wm_tab_meals_report", render: (s) => <MonthlyGrid scope={s} type="meals" /> },
    coverage: { icon: CalendarCheck, label: "wm_tab_coverage", render: (s) => <DayCoverageTable scope={s} /> },
    admin: { icon: Boxes, label: "wm_tab_admin", render: () => <AdminTab /> },
};

/**
 * Waste & Staff Meals section — «الهدر وأكل العمال» (moved verbatim out of
 * the former page component; the hub shell owns the page header now).
 *
 * Sub-tabs split by RECORD TYPE the way the operations manager's own menu is
 * organised (never by entry mode): record waste · waste report · staff meals ·
 * staff meals report · today's coverage (`wm_branch_manage`+) · item
 * management (`wm_admin`).
 *
 * A pure catalogue admin (spec level 4: `wm_admin`, no branch of their own)
 * has NO branch scope, so every record tab would 403. They still get the hub —
 * with the item-management tab alone — instead of the dead-end "no branch"
 * note that used to hide their only screen from them.
 */
export function WasteMealsSection() {
    const { t, dir } = useI18n();
    const [searchParams, setSearchParams] = useSearchParams();
    const [scope, setScope] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let alive = true;
        api.get("/waste-meals/scope")
            .then((r) => { if (alive) setScope(r.data); })
            .catch(() => { if (alive) setScope(null); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, []);

    const noBranch = !scope || scope.state === "no_home_branch";
    const canAdmin = !!scope?.caps?.can_admin;

    const tabs = useMemo(() => {
        if (noBranch) return canAdmin ? ["admin"] : [];
        // The two report tabs: masters, or the `wm_reports` add-on (2026-10-13).
        const reports = !!scope?.caps?.can_view_reports;
        const list = reports ? ["waste", "waste_report", "meals", "meals_report"] : ["waste", "meals"];
        if (scope?.caps?.can_manage_branch) list.push("coverage");
        if (canAdmin) list.push("admin");
        return list;
    }, [scope, noBranch, canAdmin]);

    const requested = searchParams.get("tab");
    const active = tabs.includes(requested) ? requested : tabs[0];

    const onTabChange = (val) => {
        const next = new URLSearchParams(searchParams);
        next.set("tab", val);
        setSearchParams(next, { replace: true });
    };

    if (loading) return <InlineLoader label={t("loading")} />;

    if (!tabs.length) {
        return (
            <div data-testid={C.noHomeBranch}
                 className="rounded-[var(--radius-lg)] border border-dashed border-[color:var(--border-soft)] bg-[color:var(--bg-surface-2)] px-6 py-12 text-center">
                <p className="text-sm font-semibold text-[color:var(--ink-muted)]">{t("wm_no_home_branch")}</p>
            </div>
        );
    }

    return (
        <div data-testid={C.hub} className="space-y-5">
            {noBranch ? (
                <p data-testid={C.adminOnlyNote}
                   className="rounded-[var(--radius-lg)] border border-dashed border-[color:var(--border-soft)] bg-[color:var(--bg-surface-2)] px-4 py-3 text-xs font-semibold text-[color:var(--ink-muted)]">
                    {t("wm_admin_only_note")}
                </p>
            ) : null}
            {/* Radix Tabs stamps dir="ltr" on its root unless told otherwise —
                the stock count's lesson: pass the page direction. */}
            <Tabs value={active} onValueChange={onTabChange} dir={dir} className="space-y-5">
                <TabsList className="inline-flex h-auto w-full flex-wrap justify-start gap-1 rounded-full border border-[color:var(--border-soft)] bg-[color:var(--bg-surface)] p-1 sm:w-auto">
                    {tabs.map((k) => {
                        const Icon = TAB_DEFS[k].icon;
                        return (
                            <TabsTrigger key={k} data-testid={C.tab(k)} value={k} className={triggerClass}>
                                <Icon className="h-4 w-4" />
                                {t(TAB_DEFS[k].label)}
                            </TabsTrigger>
                        );
                    })}
                </TabsList>

                {tabs.map((k) => (
                    <TabsContent key={k} value={k} className="mt-0 focus-visible:outline-none">
                        {TAB_DEFS[k].render(scope)}
                    </TabsContent>
                ))}
            </Tabs>
        </div>
    );
}
